/**
 * KYC Formatter
 * Transforme les données API KYC (rider & restaurant) en format uniforme pour le dashboard
 */

/**
 * Parse KYC documents from combined API data (rider + restaurant)
 */
export function parseExternalKycDocuments(raw) {
  if (!raw?.data) return null
  if (!Array.isArray(raw?.data)) return null

  return {
    kycDocuments: raw.data.map(el => {
      const baseData = {
        id: el.id || el.userId,
        userId: el.userId,
        type: el.type, // 'rider' or 'restaurant'
        kycStatus: el.kycStatus || el.status || 'pending',
        isValidated: el.isValidated || false,
        uploadedAt: el.uploadedAt || el.createdAt || Date.now(),
        validatedAt: el.validatedAt || null,
        kycData: el.kycData || el.data || {},
      }

      // Rider specific fields
      if (el.type === 'rider') {
        return {
          ...baseData,
          userName: el.fullName || el.kycData?.fullName || '-',
          userEmail: el.kycData?.email || '-',
          phoneNumber: el.phoneNumber || el.kycData?.phoneNumber || '-',
          // Rider KYC data
          fullName: el.kycData?.fullName || '',
          birthDate: el.kycData?.birthDate || '',
          gender: el.kycData?.gender || '',
          nationality: el.kycData?.nationality || '',
          address: el.kycData?.address || '',
          country: el.kycData?.country || '',
          city: el.kycData?.city || '',
          idType: el.kycData?.idType || '',
          idNumber: el.kycData?.idNumber || '',
          idExpiryDate: el.kycData?.idExpiryDate || '',
          vehicleType: el.kycData?.vehicleType || '',
          licenseNumber: el.kycData?.licenseNumber || '',
          licenseExpiry: el.kycData?.licenseExpiry || '',
          vehicleRegistration: el.kycData?.vehicleRegistration || '',
          emergencyContact: el.kycData?.emergencyContact || '',
          bankAccount: el.kycData?.bankAccount || '',
        }
      }

      // Restaurant specific fields
      if (el.type === 'restaurant') {
        return {
          ...baseData,
          userName: el.storeName || el.kycData?.storeName || '-',
          userEmail: el.kycData?.userEmail || '-',
          phoneNumber: el.phoneNumber || el.kycData?.phoneNumber || '-',
          // Restaurant KYC data
          storeName: el.kycData?.storeName || '',
          firstName: el.kycData?.firstName || '',
          lastName: el.kycData?.lastName || '',
          companyName: el.kycData?.companyName || '',
          countryCode: el.kycData?.countryCode || '',
          addresss: el.kycData?.addresss || '',
          state: el.kycData?.state || '',
          city: el.kycData?.city || '',
          postalCode: el.kycData?.postalCode || '',
          website: el.kycData?.website || '',
          pictures: el.kycData?.pictures || [],
        }
      }

      return baseData
    }),
  }
}

/**
 * Map status to display name
 */
export function getKycStatusDisplay(status) {
  if (!status) return '-'
  const normalized = String(status).toLowerCase()
  const statusMap = {
    'approved': 'Approuvé',
    'validated': 'Validé',
    'valide': 'Validé',
    'validé': 'Validé',
    'submitted': 'Soumis',
    'pending': 'En attente',
    'en attente': 'En attente',
    'filled': 'En attente',
    'under_review': 'En cours d\'examen',
    'rejected': 'Rejeté',
    'rejete': 'Rejeté',
    'rejeté': 'Rejeté',
    'draft': 'Brouillon',
    'not_filled': 'Non rempli',
    'notfilled': 'Non rempli',
  }
  return statusMap[normalized] || status
}

/**
 * Get status badge color
 */
export function getKycStatusColor(status) {
  if (!status) return { background: '#F5F5F5', color: '#616161' }
  const normalized = String(status).toLowerCase()
  const colorMap = {
    'approved': { background: '#DEF7EC', color: '#03543F' },
    'validated': { background: '#E8FFF3', color: '#2E7D32' },
    'valide': { background: '#E8FFF3', color: '#2E7D32' },
    'validé': { background: '#E8FFF3', color: '#2E7D32' },
    'submitted': { background: '#E1EFFE', color: '#1E429F' },
    'pending': { background: '#FFF9E6', color: '#DA8C00' },
    'en attente': { background: '#FFF9E6', color: '#DA8C00' },
    'filled': { background: '#FFF9E6', color: '#DA8C00' },
    'under_review': { background: '#FEF08A', color: '#854D0E' },
    'rejected': { background: '#FFE6E6', color: '#D32F2F' },
    'rejete': { background: '#FFE6E6', color: '#D32F2F' },
    'rejeté': { background: '#FFE6E6', color: '#D32F2F' },
    'draft': { background: '#F3F4F6', color: '#4B5563' },
    'not_filled': { background: '#F5F5F5', color: '#616161' },
    'notfilled': { background: '#F5F5F5', color: '#616161' },
  }
  return colorMap[normalized] || { background: '#F5F5F5', color: '#616161' }
}

